import React, { useEffect, useState } from "react";
import { Table, Tag, Popconfirm, message, Empty } from "antd";
import type { ColumnsType } from "antd/es/table";
import superAdminService from "../../../services/super-admin/super-admin.service";
import userService from "../../../services/user/user.service";
import { handleApiError } from "../../../utils/errorHandler";
import { useTranslation } from "react-i18next";
import FooterSection from "../../shared/components/footerSection";

interface DeviceConflictUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface DeviceConflictLog {
  id: number;
  type: "device_taken" | "account_locked";
  deviceId: string;
  createdAt: string;
  user: DeviceConflictUser;
  conflictingUser?: DeviceConflictUser | null;
  company: { id: number; name: string };
}

const typeLabels: Record<DeviceConflictLog["type"], string> = {
  device_taken: "Cihaz Başkasına Ait",
  account_locked: "Hesap Başka Cihaza Kilitli",
};

const typeColors: Record<DeviceConflictLog["type"], string> = {
  device_taken: "orange",
  account_locked: "red",
};

const DeviceConflictLogs: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<DeviceConflictLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await superAdminService.getDeviceConflictLogs();
      setLogs(data);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleReset = async (userId: number) => {
    setResettingUserId(userId);
    try {
      await userService.resetDevice(userId);
      message.success("Cihaz eşlemesi sıfırlandı.");
      await fetchLogs();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setResettingUserId(null);
    }
  };

  const renderUser = (u?: DeviceConflictUser | null) =>
    u ? (
      <div>
        <div>{u.firstName} {u.lastName}</div>
        <small className="text-muted">{u.email}</small>
      </div>
    ) : "-";

  const columns: ColumnsType<DeviceConflictLog> = [
    {
      title: "Tarih",
      dataIndex: "createdAt",
      render: (v: string) => new Date(v).toLocaleString("tr-TR"),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: "Tip",
      dataIndex: "type",
      render: (type: DeviceConflictLog["type"]) => (
        <Tag color={typeColors[type]}>{typeLabels[type]}</Tag>
      ),
      filters: [
        { text: typeLabels.device_taken, value: "device_taken" },
        { text: typeLabels.account_locked, value: "account_locked" },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Giriş Yapmaya Çalışan",
      dataIndex: "user",
      render: (u: DeviceConflictUser) => renderUser(u),
    },
    {
      title: "Şirket",
      dataIndex: ["company", "name"],
      render: (_: any, record) => record.company?.name || "-",
    },
    {
      title: "Cihazın Asıl Sahibi",
      dataIndex: "conflictingUser",
      render: (u?: DeviceConflictUser | null) => renderUser(u),
    },
    {
      title: "Cihaz ID",
      dataIndex: "deviceId",
      render: (deviceId: string) => <span title={deviceId}>{deviceId}</span>,
    },
    {
      title: "İşlem",
      render: (_: any, record: DeviceConflictLog) => {
        const renderResetButton = (targetUser: DeviceConflictUser, label: string) => (
          <Popconfirm
            key={targetUser.id}
            title="Cihazı Sıfırla"
            description={`${targetUser.firstName} ${targetUser.lastName} kullanıcısının cihaz eşlemesini sıfırlamak istediğinize emin misiniz?`}
            okText="Evet"
            cancelText="Vazgeç"
            okButtonProps={{ style: { backgroundColor: "#7338a0" } }}
            onConfirm={() => handleReset(targetUser.id)}
          >
            <button
              className="btn btn-sm btn-outline-primary"
              disabled={resettingUserId === targetUser.id}
              title={`Cihazı Sıfırla (${targetUser.firstName} ${targetUser.lastName})`}
            >
              <i className="ti ti-reload me-1" />
              {label}
            </button>
          </Popconfirm>
        );

        // "Cihaz başkasına ait" durumunda asıl sorun cihazın hâlâ kayıtlı olduğu kişide,
        // ama girmeye çalışanın kendi hesabı da AYRICA başka bir cihaza kilitli olabilir —
        // bileşik durumlarda admin ikisini de buradan tek tek sıfırlayabilsin diye iki buton.
        if (record.type === "device_taken" && record.conflictingUser) {
          return (
            <div className="d-flex gap-2">
              {renderResetButton(record.conflictingUser, "Cihaz Sahibi")}
              {renderResetButton(record.user, "Giriş Yapan")}
            </div>
          );
        }

        return renderResetButton(record.user, "Sıfırla");
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 className="mb-1">Cihaz ID Sıfırlama</h5>
            <p className="text-muted mb-0">
              Bir çalışan, kayıtlı olmayan/başkasına ait bir cihazdan giriş yapmaya çalıştığında burada listelenir. Giriş yapamayan kullanıcının cihaz eşlemesini buradan sıfırlayabilirsiniz.
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={logs}
              loading={loading}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: <Empty description="Kayıtlı cihaz çakışması yok" /> }}
            />
          </div>
        </div>
      </div>
      <FooterSection />
    </div>
  );
};

export default DeviceConflictLogs;
